import { useFormik } from 'formik';
import React from 'react';

import * as ManifestUI from '@project44/manifest-ds';
import { Authority, User } from '@project44/ui-utils';
import { DtoBase, UserModels } from '@project44-models/iam-gateway';

import { UIFormClient } from '../../../../../external/UIFormClient/domain/abstract/UIFormClient';
import { IFormClient } from '../../../../../external/UIFormClient/domain/interface';
import { HTMLFormElementsFactory } from '../../../../../external/UILibrary/infrastructure/factories/HTMLFormElements.factory';
import { IUserCreationForm, TUseUserCreator } from '../../../domain/interfaces';
import { UserI18NPort } from '../../ports/User.i18n.port';
import { UserCreationFormComponent } from './UserCreationForm.component';

export type TUserCreationFormView = React.PropsWithChildren<{
  UserFormCreator: UIFormClient<IUserCreationForm>;
  userPrincipal: User;
  useUserCreatorConfig: ReturnType<TUseUserCreator>;
  transformUserCreationForm: (
    userCreationForm: IUserCreationForm,
  ) => IUserCreationForm;
  userCreationFormComponent: UserCreationFormComponent;
  afterUserCreationActionHandler?: (
    user: DtoBase<UserModels.UserViewModel>,
  ) => void;
}>;

export const UserCreationFormView: React.FC<TUserCreationFormView> = ({
  UserFormCreator,
  userPrincipal,
  useUserCreatorConfig,
  transformUserCreationForm,
  userCreationFormComponent,
  afterUserCreationActionHandler,
}) => {
  const { spaceMedium } = ManifestUI.designTokens;
  const [shouldRedirect, setShouldRedirect] = React.useState<boolean>(false);

  const [userCreatorApiDataState, userCreatorFn] = useUserCreatorConfig;

  const formClient: IFormClient = useFormik<IUserCreationForm>(
    UserFormCreator.configureFormClient(async (userCreationForm) => {
      const userCreationPayload = transformUserCreationForm(userCreationForm);

      await userCreatorFn(userCreationPayload);
      setShouldRedirect(true);
    }),
  ) as unknown as IFormClient;

  React.useEffect(() => {
    if (shouldRedirect) {
      userCreationFormComponent.redirectAfterUserCreation(
        userCreatorApiDataState,
        afterUserCreationActionHandler,
      );
      setShouldRedirect(false);
    }
  }, [
    shouldRedirect,
    afterUserCreationActionHandler,
    userCreationFormComponent,
    userCreatorApiDataState,
  ]);

  const { isLoading } = userCreatorApiDataState;

  const tenantInfoI18n = UserI18NPort.scopedTranslator(
    'userCreationForm.tenantInfo',
  );

  return (
    <ManifestUI.Grid
      as={'form'}
      aria-label={`user-creation-form`}
      gap="small"
      onSubmit={(event) => {
        event.preventDefault();
        formClient.handleSubmit(event);
      }}
      id="user-creation-form"
      css={{
        mb: spaceMedium,
      }}
    >
      <ManifestUI.GridItem
        column={12}
        children={
          <ManifestUI.Typography
            as={'h1'}
            variant={'subtitle'}
            children={UserI18NPort.scopedTranslator('userCreationForm.title')}
          />
        }
      />
      <ManifestUI.GridItem
        column={12}
        children={
          <UserFormCreator.renderHTMLFormProperty
            propertyName={'username'}
            formClient={formClient}
            isLoading={isLoading}
          />
        }
      />
      <ManifestUI.GridItem
        column={6}
        children={
          <UserFormCreator.renderHTMLFormProperty
            propertyName={'givenName'}
            formClient={formClient}
            isLoading={isLoading}
          />
        }
      />
      <ManifestUI.GridItem
        column={6}
        children={
          <UserFormCreator.renderHTMLFormProperty
            propertyName={'familyName'}
            formClient={formClient}
            isLoading={isLoading}
          />
        }
      />
      <ManifestUI.GridItem
        column={12}
        children={
          <UserFormCreator.renderHTMLFormProperty
            propertyName={'phoneNumber'}
            formClient={formClient}
            isLoading={isLoading}
          />
        }
      />
      <ManifestUI.GridItem
        column={12}
        children={
          userPrincipal.hasAnyOfPermissions(['system-admin:create:users']) ? (
            <>
              <HTMLFormElementsFactory.composeFormControl
                label={tenantInfoI18n}
              />
              <HTMLFormElementsFactory.composeTextInputField
                value={userPrincipal?.companyName}
                name={'tenantName'}
                aria-label={'tenant'}
                aria-labelledby={tenantInfoI18n}
                isReadOnly
                isDisabled
              />
            </>
          ) : undefined
        }
      />
      <ManifestUI.GridItem
        column={12}
        css={{
          marginTop: spaceMedium,
        }}
        children={
          <ManifestUI.Typography
            as={'h2'}
            variant={'subtitle'}
            children={UserI18NPort.scopedTranslator(
              'userCreationForm.password',
            )}
          />
        }
      />
      <ManifestUI.GridItem
        column={12}
        children={
          <UserFormCreator.renderHTMLFormProperty
            propertyName={'password'}
            withFormControl={false}
            formClient={formClient}
            isLoading={isLoading}
          />
        }
      />
      <ManifestUI.GridItem
        column={12}
        css={{
          marginTop: spaceMedium,
        }}
        children={
          <ManifestUI.Typography
            as={'h2'}
            variant={'subtitle'}
            children={UserI18NPort.scopedTranslator(
              'userCreationForm.userType',
            )}
          />
        }
      />
      <ManifestUI.GridItem
        column={12}
        children={
          <UserFormCreator.renderHTMLFormProperty
            propertyName={'userType'}
            formClient={formClient}
            isLoading={isLoading}
          />
        }
      />
      <ManifestUI.GridItem
        column={12}
        css={{
          marginTop: spaceMedium,
        }}
        children={
          userPrincipal.hasAnyOfPermissions([
            'system-admin:update:tenant-specific-user-role-assignments' as unknown as Authority,
            'SAVE_USER_ROLES' as unknown as Authority,
          ]) && (
            <ManifestUI.Typography
              as={'h2'}
              variant={'subtitle'}
              children={UserI18NPort.scopedTranslator(
                'userCreationForm.access',
              )}
            />
          )
        }
      />
      <ManifestUI.GridItem
        column={12}
        children={
          <UserFormCreator.renderHTMLFormProperty
            propertyName={'isAdmin'}
            formClient={formClient}
            isLoading={isLoading}
          />
        }
      />
    </ManifestUI.Grid>
  );
};
